import { createSlice, configureStore } from '@reduxjs/toolkit';
import PopUpModal from '../components/PopUpModal/PopUpModal';
import Product from '../components/Product/Product';

const initialVisualizerState = { 
    surfaceList : [],
    specifier:"",
    activeSurface:null,
    appliedProducts:{},
    appliedSnapshots:[],
    snapIndex:0,
    maxSnapshots: 20,
    muteNextSnapshot:false,

    activeProductMenuTab : 'flooring',
    activeFlooringSubMenuTab : null,
    activeFlooringSeriesIds: [8485],

    appliedFilters:[],
    lastAppliedFilter:"",
    patternData:[],
    activePattern:null,

    productDataBySeries:{},
    productDropdownFilters:{},
    productSearchFilters:{},

    flooringTypeFilterObj:{},
    flooringTypes:{},

    compareData:{
      side:'chamViz',
      active:false
    },

    shareData:{
      specifier:"",
      sel:-1,
      url:"",
      message:"Check out this design I made using the Southern Luxe Visualizer!"
    }



};

const visualizerSlice = createSlice({
  name: 'visualizer',
  initialState: initialVisualizerState,

  reducers: {
    
    loadBackup(state, actions){
        if (!actions.payload) return;
        
        state.specifier = actions.payload.specifier || state.specifier;
        state.surfaceList = actions.payload.surfaceList || state.surfaceList;
        state.activeSurface = actions.payload.activeSurface !== undefined ? actions.payload.activeSurface : state.activeSurface;
        state.appliedProducts = actions.payload.appliedProducts || state.appliedProducts;
        state.appliedSnapshots = actions.payload.appliedSnapshots || state.appliedSnapshots;
        state.activeProductMenuTab = actions.payload.activeProductMenuTab || state.activeProductMenuTab;
        state.activeFlooringSubMenuTab = actions.payload.activeFlooringSubMenuTab || state.activeFlooringSubMenuTab;
        state.activeFlooringSeriesIds = actions.payload.activeFlooringSeriesIds || state.activeFlooringSeriesIds;
    },

    setSurfaceList(state,action) {
      state.surfaceList = action.payload;
    },
    setSpecifier(state,action){
        state.specifier = action.payload;
    },
    setActiveSurface(state,action){
      state.activeSurface = action.payload;
    },

    setProductDataBySeries(state, action){
      state.productDataBySeries[action.payload.seriesId] = action.payload.data;
    },

    setAppliedProduct(state, action) {
      const { location, surface, data, swapData } = action.payload;

      if (swapData == null) {
        if (!state.appliedProducts[location]) state.appliedProducts[location] = {};
        state.appliedProducts[location][surface] = data;
      } else {
        // shallow clone to avoid frozen reference
        state.appliedProducts[location] = { ...swapData };
        // If values are nested objects you plan to mutate, clone those too:
        // state.appliedProducts[location] = Object.fromEntries(
        //   Object.entries(swapData).map(([k, v]) => [k, { ...v }])
        // );
      }
    },

    updateAppliedProductDataVal(state, action) {
      const { location, surface, key, value } = action.payload;
      state.appliedProducts[location][surface][key] = value;
    },

    // clear everything
    clearAppliedProducts(state) {
      state.appliedProducts = {};
    },

    clearAppliedProductsByLocation(state, action) {
      delete state.appliedProducts[action.payload];
    },

    // clear one surface at one location (simple + safe)
    clearAppliedProductsBySurface(state, action) {
      const { location, surface } = action.payload || {};
      if (!location || !surface) return;

      const bucket = state.appliedProducts[location];
      if (!bucket) return;

      delete bucket[surface];

      // optional: prune empty location
      if (Object.keys(bucket).length === 0) {
        delete state.appliedProducts[location];
      }
    },

    // Take a snapshot of current appliedProducts
    snapshotAppliedProducts(state) {

      if(state.muteNextSnapshot){
        state.muteNextSnapshot = false;
        return;
      }

      // Deep copy to avoid reference issues
      const snap = JSON.parse(JSON.stringify(state.appliedProducts || {}));
      
      // if we've undone into history, discard any "future" snapshots
      if (state.snapIndex < state.appliedSnapshots.length - 1) {
        state.appliedSnapshots = state.appliedSnapshots.slice(0, state.snapIndex + 1);
      }

      state.appliedSnapshots.push({
        products: snap,
        activeSurface: state.activeSurface,
        menuTab: state.activeProductMenuTab,
        flooringSubMenuTab: state.activeFlooringSubMenuTab,
        flooringSeriesIds: state.activeFlooringSeriesIds
      });

      // cap history size (drop oldest)
      if (state.appliedSnapshots.length > state.maxSnapshots) {
        state.appliedSnapshots.shift();
        // adjust pointer because we removed index 0
        state.snapIndex = state.appliedSnapshots.length - 1;
      } else {
        state.snapIndex = state.appliedSnapshots.length - 1;
      }
    },

    // Undo: step backward in history
    goBackward(state) {
      if (state.snapIndex <= 0) return; // nothing older
      state.snapIndex -= 1;

      state.muteNextSnapshot = true;
      const snapshot = state.appliedSnapshots[state.snapIndex];
      
      if (!snapshot) return; // safety check


      state.activeSurface = snapshot.activeSurface;
      state.activeProductMenuTab = snapshot.menuTab;
      state.activeFlooringSubMenuTab = snapshot.flooringSubMenuTab;
      state.activeFlooringSeriesIds = snapshot.flooringSeriesIds;
      state.appliedProducts = snapshot.products || {};
      
    },

    // Redo: step forward in history
    goForward(state) {
      if (state.snapIndex >= state.appliedSnapshots.length - 1) return; // nothing newer
      state.snapIndex += 1;

      state.muteNextSnapshot = true;
      const snapshot = state.appliedSnapshots[state.snapIndex];
      
      if (!snapshot) return; // safety check
      
      state.activeSurface = snapshot.activeSurface;
      state.activeProductMenuTab = snapshot.menuTab;
      state.activeFlooringSubMenuTab = snapshot.flooringSubMenuTab;
      state.activeFlooringSeriesIds = snapshot.flooringSeriesIds;
      state.appliedProducts = snapshot.products || {};
    },


    clearAppliedSnapshots(state) {
      state.appliedSnapshots = [];
      state.muteNextSnapshot = false;
    },


    setflooringTypes(state, action){
      state.flooringTypes = action.payload;
    },

    setActiveProductMenuTab(state, action) {
      state.activeProductMenuTab = action.payload;
    },

    setActiveFlooringSubMenuTab(state, action) {
      state.activeFlooringSubMenuTab = action.payload;
    },

    setActiveFlooringSeriesIds(state, action) {
      state.activeFlooringSeriesIds = action.payload;
    },

    setCompareData(state, action) {
      if (!state.compareData) {
        state.compareData = {};  // initialize if missing
      }
      state.compareData[action.payload.key] = action.payload.value;
    },

  
    setproductDropdownFilter(state, action) {
      state.productDropdownFilters = {
          ...state.productDropdownFilters, // preserve existing
          ...action.payload, // merge new filter
      };
    },

    setflooringTypeFilterObj(state, action){
      state.flooringTypeFilterObj = action.payload;
    },
    
    
    clearProductDropdownFilters(state, action) {
      const keepIndexes = action.payload;
      //remove any by index and exclude any indexes in keepIndexes
      state.productDropdownFilters = Object.keys(state.productDropdownFilters)
          .filter(key => keepIndexes.includes(key))
          .reduce((filtered, key) => {
              filtered[key] = state.productDropdownFilters[key];
              return filtered;
          }, {});
    },

    setProductSearchFilter(state, action) {
      state.productSearchFilters = {
          ...state.productSearchFilters, // preserve existing
          ...action.payload, // merge new filter
      };
    },

    clearProductSearchFilters(state, action){
      state.productSearchFilters = {};
    },

    //-------------------------------------

    // Applied filters state handlers
    addAppliedFilter(state, action) {
      var filter = action.payload;
      state.appliedFilters = [...state.appliedFilters, filter];
    },

    updateAppliedFilterByIndex(state, action) {
      state.appliedFilters[action.payload.index] = action.payload.filter;
    },

    removeAppliedFilter(state, action) {
      state.appliedFilters = state.appliedFilters.filter(
        (f) => !(f.name === action.payload.name && f.value === action.payload.value)
      );
    },

    setLastAppliedFilter(state, action) {
      state.lastAppliedFilter = action.payload;
    },

    removeAppliedFilterByGroupId(state, action) {
      state.appliedFilters = state.appliedFilters.filter(
        (f) => !(f.groupId === action.payload)
      );
    },

    clearAppliedFilterByName(state, action){
      state.appliedFilters = state.appliedFilters.filter(
        (f) => f.name !== action.payload.name
      );
    },

    resetAppliedFilters(state, action){
      state.appliedFilters = [];
    },

    //------------------------------------


    // Set share data to be used from social share options & email
    setShareData(state,action){
      state.shareData = action.payload;
    }


  },
});


const initialUIState = {
  toolName:"React Base Tool",
  vizLoading: false,
  popUpModalActive : false,
  shareModalActive : false,
  emailModalActive : false,
  projectsModalActive : false,
  zoomModalActive : false,
  errorModalActive : false,
  errorModalMessage : "",
  socialShareText : "Checkout this design I made using the React Base Tool Visualizer!",
  loadingScreenActive : true,
  

};

const uiSlice = createSlice({
  name: 'ui',
  initialState: initialUIState,
  reducers: {
    setLoadingScreenActive(state, action) {
      state.loadingScreenActive = action.payload;
    },
    setToolName(state, action) {
      state.toolName = action.payload;
    },
    setVizLoading(state, action) {
      state.vizLoading = action.payload;
    },
    setShareModalActive(state, action){
      state.shareModalActive = action.payload;
    },
    setPopUpModalActive(state, action){
      state.popUpModalActive = action.payload;
    },
    setEmailModalActive(state, action){
      state.emailModalActive = action.payload;
    },
    setProjectsModalActive(state, action){
      state.projectsModalActive = action.payload;
    },
    setZoomModalActive(state, action){
      state.zoomModalActive = action.payload;
    },
    setPopUpModalStatus(state, action){
      state.showPopUpModal = action.payload;
    },
    setEmailShareModalStatus(state, action){
      state.showEmailShareModal = action.payload;
    },
    setFeedbackModalActive(state, action){
      state.feedbackModalActive = action.payload;
    },
    setHelpModalActive(state, action){
      state.helpModalActive = action.payload;
    },
    setErrorModalActive(state, action){
      state.errorModalActive = action.payload;
    },
    setErrorModalMessage(state, action){
      state.errorModalMessage = action.payload;
    },
    

  },
});


const store = configureStore({
  reducer: {
    visualizer: visualizerSlice.reducer,
    ui: uiSlice.reducer
  }
});

export const visualizerActions = visualizerSlice.actions;
export const uiActions = uiSlice.actions;

export default store;